import { useRef, useState, useCallback } from "react";

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
}

export default function RichTextEditor({ value, onChange, placeholder = "Write something..." }: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const [activeCommands, setActiveCommands] = useState<Set<string>>(new Set());
  const [showImageInput, setShowImageInput] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [lastSelection, setLastSelection] = useState<Range | null>(null);

  const exec = useCallback((command: string, valueArg: string = "") => {
    document.execCommand(command, false, valueArg);
    updateActiveStates();
    syncContent();
  }, []);

  const updateActiveStates = () => {
    const commands = [
      "bold", "italic", "underline", "strikeThrough",
      "insertUnorderedList", "insertOrderedList",
      "justifyLeft", "justifyCenter", "justifyRight",
    ];
    const active = new Set<string>();
    commands.forEach((cmd) => {
      if (document.queryCommandState(cmd)) active.add(cmd);
    });
    const formatBlock = document.queryCommandValue("formatBlock").toLowerCase();
    if (formatBlock === "h1") active.add("h1");
    if (formatBlock === "h2") active.add("h2");
    if (formatBlock === "h3") active.add("h3");
    setActiveCommands(active);
  };

  const syncContent = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const handleFocus = () => {
    updateActiveStates();
  };

  const handleKeyUp = () => {
    updateActiveStates();
    syncContent();
  };

  const handleMouseUp = () => {
    updateActiveStates();
    syncContent();
    saveSelection();
  };

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      setLastSelection(sel.getRangeAt(0).cloneRange());
    }
  };

  const insertImage = () => {
    if (!imageUrl.trim()) return;
    restoreSelection();
    exec("insertImage", imageUrl.trim());
    setImageUrl("");
    setShowImageInput(false);
  };

  const insertLink = () => {
    if (!linkUrl.trim()) return;
    restoreSelection();
    exec("createLink", linkUrl.trim());
    setLinkUrl("");
    setShowLinkInput(false);
  };

  const restoreSelection = () => {
    if (!lastSelection) return;
    const sel = window.getSelection();
    if (sel) {
      sel.removeAllRanges();
      sel.addRange(lastSelection);
    }
  };

  const btnClass = (cmd: string, isBlock: boolean = false) => {
    const isActive = isBlock
      ? activeCommands.has(cmd)
      : activeCommands.has(cmd);
    const base = "w-8 h-8 flex items-center justify-center rounded-lg text-sm transition-all duration-150 cursor-pointer";
    return isActive
      ? `${base} bg-primary text-white`
      : `${base} text-stone-500 hover:bg-stone-100 hover:text-stone-700`;
  };

  return (
    <div className="w-full border border-stone-200 rounded-xl overflow-hidden bg-white">
      {/* Toolbar */}
      <div className="flex items-center gap-1 px-3 py-2 border-b border-stone-100 bg-stone-50 flex-wrap">
        <div className="flex items-center gap-0.5">
          <button type="button" onClick={() => exec("bold")} className={btnClass("bold")} title="Bold">
            <i className="ri-bold text-xs"></i>
          </button>
          <button type="button" onClick={() => exec("italic")} className={btnClass("italic")} title="Italic">
            <i className="ri-italic text-xs"></i>
          </button>
          <button type="button" onClick={() => exec("underline")} className={btnClass("underline")} title="Underline">
            <i className="ri-underline text-xs"></i>
          </button>
          <button type="button" onClick={() => exec("strikeThrough")} className={btnClass("strikeThrough")} title="Strikethrough">
            <i className="ri-strikethrough text-xs"></i>
          </button>
        </div>

        <div className="w-px h-5 bg-stone-200 mx-1" />

        <div className="flex items-center gap-0.5">
          <button type="button" onClick={() => exec("formatBlock", "H1")} className={btnClass("h1", true)} title="Heading 1">
            <i className="ri-heading text-xs">1</i>
          </button>
          <button type="button" onClick={() => exec("formatBlock", "H2")} className={btnClass("h2", true)} title="Heading 2">
            <i className="ri-heading text-xs">2</i>
          </button>
          <button type="button" onClick={() => exec("formatBlock", "H3")} className={btnClass("h3", true)} title="Heading 3">
            <i className="ri-heading text-xs">3</i>
          </button>
          <button type="button" onClick={() => exec("formatBlock", "P")} className={btnClass("p", true)} title="Paragraph">
            <i className="ri-paragraph text-xs"></i>
          </button>
        </div>

        <div className="w-px h-5 bg-stone-200 mx-1" />

        <div className="flex items-center gap-0.5">
          <button type="button" onClick={() => exec("insertUnorderedList")} className={btnClass("insertUnorderedList")} title="Bullet List">
            <i className="ri-list-unordered text-xs"></i>
          </button>
          <button type="button" onClick={() => exec("insertOrderedList")} className={btnClass("insertOrderedList")} title="Numbered List">
            <i className="ri-list-ordered text-xs"></i>
          </button>
        </div>

        <div className="w-px h-5 bg-stone-200 mx-1" />

        <div className="flex items-center gap-0.5">
          <button
            type="button"
            onClick={() => {
              saveSelection();
              setShowLinkInput(!showLinkInput);
              setShowImageInput(false);
            }}
            className={`${btnClass("link")} ${showLinkInput ? "bg-primary text-white" : ""}`}
            title="Insert Link"
          >
            <i className="ri-link text-xs"></i>
          </button>
          <button
            type="button"
            onClick={() => {
              saveSelection();
              setShowImageInput(!showImageInput);
              setShowLinkInput(false);
            }}
            className={`${btnClass("image")} ${showImageInput ? "bg-primary text-white" : ""}`}
            title="Insert Image"
          >
            <i className="ri-image-line text-xs"></i>
          </button>
        </div>
      </div>

      {/* Link Input */}
      {showLinkInput && (
        <div className="flex items-center gap-2 px-4 py-2 border-b border-stone-100 bg-amber-50/50">
          <i className="ri-link text-stone-400 text-xs"></i>
          <input
            type="text"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="https://example.com"
            className="flex-1 text-sm px-3 py-1.5 rounded-lg border border-stone-200 bg-white focus:outline-none focus:border-primary"
            onKeyDown={(e) => e.key === "Enter" && insertLink()}
          />
          <button onClick={insertLink} className="px-3 py-1.5 rounded-lg bg-primary text-white text-xs font-semibold hover:bg-primary-dark cursor-pointer transition-colors">
            Insert
          </button>
          <button onClick={() => { setShowLinkInput(false); setLinkUrl(""); }} className="px-2 py-1.5 text-stone-400 hover:text-stone-600 text-xs cursor-pointer">
            <i className="ri-close-line"></i>
          </button>
        </div>
      )}

      {/* Image Input */}
      {showImageInput && (
        <div className="flex items-center gap-2 px-4 py-2 border-b border-stone-100 bg-amber-50/50">
          <i className="ri-image-line text-stone-400 text-xs"></i>
          <input
            type="text"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            placeholder="Paste image URL..."
            className="flex-1 text-sm px-3 py-1.5 rounded-lg border border-stone-200 bg-white focus:outline-none focus:border-primary"
            onKeyDown={(e) => e.key === "Enter" && insertImage()}
          />
          <button onClick={insertImage} className="px-3 py-1.5 rounded-lg bg-primary text-white text-xs font-semibold hover:bg-primary-dark cursor-pointer transition-colors">
            Insert
          </button>
          <button onClick={() => { setShowImageInput(false); setImageUrl(""); }} className="px-2 py-1.5 text-stone-400 hover:text-stone-600 text-xs cursor-pointer">
            <i className="ri-close-line"></i>
          </button>
        </div>
      )}

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={syncContent}
        onFocus={handleFocus}
        onKeyUp={handleKeyUp}
        onMouseUp={handleMouseUp}
        onClick={saveSelection}
        className="min-h-[200px] px-4 py-3 text-sm text-stone-800 leading-relaxed focus:outline-none prose-editor"
        dangerouslySetInnerHTML={{ __html: value || "" }}
        data-placeholder={placeholder}
      />
    </div>
  );
}